import { Component, Input, OnInit, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {
  LiveClass,
  Meeting,
} from '../../core/state/live-class/models/live-class';
import { MeetingUtility } from '../../core/state/live-class/models/live-class.dto';
import { DateTime } from 'luxon';
import { ToastService } from '@black/ui';
import {
  PaymentType,
  Subscription,
  SubscriptionStatus,
} from '../../core/state/subscription/subscription.model';
import {
  getNextSessionEventDate,
  getSessionCountdown,
  SessionCountdown,
  formatTimeString,
  getEffectiveMeetingAccess,
  MeetingAccessResult,
} from '@black/shared';

@Component({
  selector: 'black-meetings-list-view',
  standalone: false,
  templateUrl: './meetings-list-view.component.html',
  styleUrl: './meetings-list-view.component.css',
  host: {
    ngSkipHydration: 'true',
  },
})
export class MeetingsListViewComponent implements OnInit, OnDestroy {
  private toastService = inject(ToastService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  @Input({ required: true })
  liveClass!: LiveClass;

  now: Date = new Date();
  private timerHandle: any;

  isPassModalOpen = false;
  selectedPassDetails: {
    liveClassName?: string;
    liveClassId?: string;
    meetingName?: string;
    targetEventDate?: Date;
    startTime?: string;
    endTime?: string;
    directJoinUrl?: string;
    shareLinkId?: string;
    provider?: string;
    status: string;
    meeting?: Meeting;
  } | null = null;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.timerHandle = setInterval(() => {
        this.now = new Date();
      }, 10000);
    }
  }

  ngOnDestroy(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
    }
  }

  getMeetingAccess(meeting: Meeting): MeetingAccessResult {
    const nextDate = this.getNextMeetingDate(meeting);
    return getEffectiveMeetingAccess({
      subscriptions: this.liveClass?.subscriptions,
      meeting,
      targetDate: nextDate,
      now: this.now,
    });
  }

  get hasFullCourseAccess(): boolean {
    if (!this.liveClass?.subscriptions || !Array.isArray(this.liveClass.subscriptions)) {
      return false;
    }
    return this.liveClass.subscriptions.some(
      (sub) =>
        sub.paymentType !== PaymentType.SINGLE_EVENT &&
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(sub.status)
    );
  }

  getSinglePassForMeeting(meeting: Meeting): Subscription | null {
    const access = this.getMeetingAccess(meeting);
    if (access.accessType === 'SINGLE_EVENT') {
      return access.effectiveSubscription || null;
    }
    return null;
  }

  isPendingSubscription(): boolean {
    const subs = this.liveClass?.subscriptions;
    if (!Array.isArray(subs)) return false;
    const hasActive = subs.some((s) =>
      [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(
        s.status,
      ),
    );
    return !hasActive && subs.some((s) => s.status === SubscriptionStatus.PENDING);
  }

  isMeetingPending(meeting: Meeting): boolean {
    return this.getMeetingAccess(meeting).isPending;
  }

  isSingleEventSubscriber(): boolean {
    return (
      !this.hasFullCourseAccess &&
      Array.isArray(this.liveClass?.subscriptions) &&
      this.liveClass!.subscriptions.some(
        (sub) =>
          sub.paymentType === PaymentType.SINGLE_EVENT &&
          [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(sub.status)
      )
    );
  }

  isFreeMeeting(meeting: Meeting): boolean {
    return (
      meeting.price === 0 ||
      (meeting.price == null && (this.liveClass?.settings?.price ?? 0) === 0)
    );
  }

  isMeetingAccessible(meeting: Meeting): boolean {
    return this.getMeetingAccess(meeting).hasAccess;
  }

  getTargetEventDate(meeting: Meeting): Date | null {
    const access = this.getMeetingAccess(meeting);
    return access.singlePassDate || null;
  }

  getNextMeetingDate(meeting: Meeting): Date | null {
    return getNextSessionEventDate(meeting as any, { now: this.now });
  }

  getMeetingCountdown(meeting: Meeting): SessionCountdown | null {
    const nextDate = this.getNextMeetingDate(meeting);
    if (!nextDate) return null;
    return getSessionCountdown(nextDate, {
      startTime: meeting.startTime,
      endTime: meeting.endTime,
      timezone: meeting.timezone,
      now: this.now,
    });
  }

  openPassModal(meeting: Meeting) {
    const access = this.getMeetingAccess(meeting);
    this.selectedPassDetails = {
      liveClassName: this.liveClass?.title,
      liveClassId: this.liveClass?.id,
      meetingName: meeting.name,
      targetEventDate: access.singlePassDate || undefined,
      startTime: meeting.startTime,
      endTime: meeting.endTime,
      directJoinUrl: meeting.directJoinUrl,
      shareLinkId: meeting.shareLinkId,
      provider: meeting.provider,
      status: 'Active Single Pass',
      meeting,
    };
    this.isPassModalOpen = true;
  }

  closePassModal() {
    this.isPassModalOpen = false;
    this.selectedPassDetails = null;
  }

  navigateToUpgrade() {
    this.closePassModal();
    if (this.liveClass?.id) {
      this.router.navigate(['live-classes', this.liveClass.id, 'subscribe']);
    }
  }

  onBuyPassForSession(meeting: Meeting) {
    if (this.liveClass?.id) {
      this.router.navigate(['live-classes', this.liveClass.id, 'subscribe'], {
        queryParams: {
          plan: 'single',
          meetingId: meeting.id || (meeting as any)._id,
        },
      });
    }
  }

  getMeetingDescription(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);
    return meetingUtility.toDescription();
  }

  getMeetingInterval(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);

    const diffInMinutes = meetingUtility.getSessionInterval();

    if (diffInMinutes < 60) return `${diffInMinutes} Minutes`;

    return `${Math.floor(diffInMinutes / 60)} hour ${
      diffInMinutes % 60
    } Minutes`;
  }

  getStartDate(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);

    const startDateTime = meetingUtility.getStartDate();
    const intervalInMinutes = meetingUtility.getSessionInterval();
    const endDateTime = DateTime.fromJSDate(startDateTime)
      .plus({ minutes: intervalInMinutes })
      .toJSDate();

    return [startDateTime, endDateTime];
  }

  isMeetingConfigured(meeting: Meeting): boolean {
    if (meeting.directJoinUrl && meeting.directJoinUrl.trim().length > 0) {
      return true;
    }
    if (meeting.provider === 'ZOOM') {
      return !!meeting.zoomMeetingId;
    }
    if (meeting.provider === 'REALTIMEKIT' || meeting.provider === 'DYTE') {
      return !!(meeting.rtkMeetingId || meeting.dyteMeetingId);
    }
    return false;
  }

  joinMeeting(meeting: Meeting) {
    if (meeting.directJoinUrl && meeting.directJoinUrl.trim().length > 0) {
      window.open(
        meeting.directJoinUrl.trim(),
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }
    if (meeting.shareLinkId) {
      this.joinMeetingByLinkId(meeting.shareLinkId, meeting.provider);
    }
  }

  joinMeetingByLinkId(shareLinkId: string, meetingProvider = 'DYTE') {
    window.open(
      `./meetings/join/${meetingProvider.toLowerCase()}/` + shareLinkId,
      '_blank',
      'noopener,noreferrer',
    );
  }

  copyShareLinkId(shareLinkId: string, meetingProvider = 'DYTE') {
    const baseUrl = window.location.origin;
    const meetingUrl = `${baseUrl}/meetings/join/${meetingProvider.toLowerCase()}/${shareLinkId}`;
    navigator.clipboard.writeText(meetingUrl);
    this.toastService.showToast('Meeting link copied to clipboard', 'success');
  }
}
